import React from "react";

import { ReactComponent as FormsIcon } from "../../../icons/FormsProfile.svg";
import { ReactComponent as StepsIcon } from "../../../icons/Steps.svg";
import { ReactComponent as NutritionTargetsIcon } from "../../../icons/NutritionTargets.svg";
import { ReactComponent as GoalsIcon } from "../../../icons/Goals.svg";
import { ReactComponent as BodyWeightIcon } from "../../../icons/bodyWeight.svg";
import { ReactComponent as MeasurementsIcon } from "../../../icons/measurementsChart.svg";
import { ReactComponent as ProgressPhotosIcon } from "../../../icons/ProgressPhotos.svg";
import { ReactComponent as WorkoutsIcon } from "../../../icons/wokoutsIcon.svg";
import { ReactComponent as ExercisesIcon } from "../../../icons/exerciseIcon.svg";
import { ReactComponent as HighlightsIcon } from "../../../icons/HighlightProfile.svg";
import { ReactComponent as ProgramsIcon } from "../../../icons/ProgramsProfile.svg";

import { Box } from "@mui/material";

export interface ProfileCardPosition {
  type: ProfileCardType;
  x: number;
  y: number;
  columns: number;
}

export interface ClientCardMeta extends ProfileCardPosition {
  title: string;
  subtitle?: string;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  withCardHeader: boolean;
  height: number;
  resizable: boolean;
  content?: React.ReactNode;
}

export enum ProfileCardType {
  WORKOUTS_HIGHLIGHTS = "workout-highlights",
  PROGRAMS = "programs",
  FORMS = "forms",
  STEPS = "steps",
  NUTRITION = "nutrition",
  MEASUREMENTS = "measurements",
  BODY_WEIGHT = "body-weight",
  GOALS = "goals",
  PROGRESS_PHOTOS = "progress-photos",
  WORKOUTS = "workouts",
  EXERCISES = "exercises",
  SPACER = "spacer",
}

export const CLIENT_PROFILE_DEFAULT_POSITIONS: ProfileCardPosition[] = [
  { type: ProfileCardType.WORKOUTS_HIGHLIGHTS, x: 0, y: 0, columns: 2 },
  { type: ProfileCardType.PROGRAMS, x: 0, y: 1, columns: 2 },
  { type: ProfileCardType.FORMS, x: 0, y: 2, columns: 1 },
  { type: ProfileCardType.STEPS, x: 1, y: 2, columns: 1 },
  { type: ProfileCardType.NUTRITION, x: 0, y: 3, columns: 2 },
  { type: ProfileCardType.GOALS, x: 0, y: 4, columns: 2 },
  { type: ProfileCardType.BODY_WEIGHT, x: 0, y: 5, columns: 1 },
  { type: ProfileCardType.MEASUREMENTS, x: 1, y: 5, columns: 1 },
  { type: ProfileCardType.PROGRESS_PHOTOS, x: 0, y: 6, columns: 2 },
  { type: ProfileCardType.WORKOUTS, x: 0, y: 7, columns: 1 },
  { type: ProfileCardType.EXERCISES, x: 1, y: 7, columns: 1 },
];

export const CLIENT_PROFILE_LAYOUT_SETUP_CARDS_DATA = (
  nutritionsEnabled?: boolean,
  positions?: ProfileCardPosition[],
): ClientCardMeta[] => {
  const posMap = new Map(
    (positions ?? CLIENT_PROFILE_DEFAULT_POSITIONS).map((p) => [p.type, p]),
  );

  const getPos = (type: ProfileCardType) => posMap.get(type);

  return [
    {
      type: ProfileCardType.WORKOUTS_HIGHLIGHTS,
      title: "Workout Highlights",
      withCardHeader: true,
      Icon: HighlightsIcon,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.WORKOUTS_HIGHLIGHTS),
    },
    {
      type: ProfileCardType.PROGRAMS,
      title: "Programs",
      withCardHeader: true,
      Icon: ProgramsIcon,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.PROGRAMS),
    },
    {
      type: ProfileCardType.FORMS,
      title: "Forms",
      Icon: FormsIcon,
      withCardHeader: true,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.FORMS),
    },
    {
      type: ProfileCardType.STEPS,
      title: "Steps",
      Icon: StepsIcon,
      withCardHeader: true,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.STEPS),
    },
    {
      type: ProfileCardType.NUTRITION,
      subtitle: nutritionsEnabled
        ? null
        : "Nutrition feature is disabled and card will not be shown on a profile",
      title: "Nutrition",
      Icon: NutritionTargetsIcon,
      withCardHeader: true,
      height: 2,
      resizable: true,
      ...getPos(ProfileCardType.NUTRITION),
    },
    {
      type: ProfileCardType.GOALS,
      title: "Goals",
      Icon: GoalsIcon,
      withCardHeader: true,
      height: 2,
      resizable: true,
      ...getPos(ProfileCardType.GOALS),
    },
    {
      type: ProfileCardType.BODY_WEIGHT,
      title: "Weight",
      Icon: BodyWeightIcon,
      withCardHeader: true,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.BODY_WEIGHT),
    },
    {
      type: ProfileCardType.MEASUREMENTS,
      title: "Measurements",
      Icon: MeasurementsIcon,
      withCardHeader: true,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.MEASUREMENTS),
    },
    {
      type: ProfileCardType.PROGRESS_PHOTOS,
      title: "Progress photos",
      Icon: ProgressPhotosIcon,
      withCardHeader: true,
      height: 2,
      resizable: true,
      ...getPos(ProfileCardType.PROGRESS_PHOTOS),
    },
    {
      type: ProfileCardType.WORKOUTS,
      title: "Workouts",
      Icon: WorkoutsIcon,
      withCardHeader: true,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.WORKOUTS),
    },
    {
      type: ProfileCardType.EXERCISES,
      title: "Exercises",
      Icon: ExercisesIcon,
      withCardHeader: true,
      height: 2,
      resizable: false,
      ...getPos(ProfileCardType.EXERCISES),
    },
  ];
};
export const withSpacers = (
  positions: ProfileCardPosition[],
  cols = 2,
): ProfileCardPosition[] => {
  const occupied = new Set<string>();

  positions.forEach((p) => {
    for (let x = p.x; x < p.x + p.columns; x++) {
      occupied.add(`${x}:${p.y}`);
    }
  });

  const spacers: ProfileCardPosition[] = [];

  positions.forEach((p) => {
    if (p.columns !== 1) return;

    const otherCol = p.x === 0 ? 1 : 0;
    const key = `${otherCol}:${p.y}`;

    if (!occupied.has(key)) {
      spacers.push({
        type: ProfileCardType.SPACER,
        x: otherCol,
        y: p.y,
        columns: 1,
      });
    }
  });

  return [...positions, ...spacers];
};
